"use client";

import { Check, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Tuition() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: reassuranceRef,
    isVisible: reassuranceVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const starterFeatures = ["Half-day morning session", "Montessori curriculum", "Nutritious morning snack", "Outdoor play time", "Small group interaction", "Progress reports"];
  const proFeatures = ["Full-day immersive program", "Morning & afternoon snacks", "Hot catered lunch included", "Extended outdoor exploration", "Individualized learning plans", "Daily communication log", "Enrichment activities", "CWELCC subsidy eligible", "Priority registration"];
  const agencyFeatures = ["Flexible 3-day schedule", "Montessori core curriculum", "Morning snack included", "Outdoor play & discovery", "Social-emotional development", "Parent-teacher conferences", "School community events", "Secure facility access"];
  const reassurance = ["Licensed facility", "Certified Montessori guides", "Nut-free environment"];
  return <section data-section-id="124" id="tuition-programs" className="relative overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 opacity-40" aria-hidden="true">
        <div className="absolute left-1/2 top-0 h-[500px] w-[800px] -translate-x-1/2 rounded-lg bg-[radial-gradient(ellipse_at_center,var(--primary)_0%,transparent_70%)] opacity-20 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[600px] rounded-lg bg-[radial-gradient(ellipse_at_center,var(--chart-2)_0%,transparent_70%)] opacity-10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="mb-5 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground font-mono uppercase tracking-wider">
            <Sparkles className="size-3 text-primary" />
            Programs
          </div>
          <h2 className="font-default text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            Tuition & <span className="font-serif italic">Programs</span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
            We offer flexible enrollment options designed to support your child's 
            developmental journey in a nurturing, Montessori-inspired environment.
          </p>
        </div>

        {}
        <div ref={gridRef} className={`mx-auto mt-20 grid max-w-md grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3 lg:items-center transition-all duration-700 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          {}
          <div data-index={0} className="relative flex flex-col rounded-3xl border border-border bg-card p-10 shadow-lg">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Half-Day
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                $850
              </span>
              <span className="text-base text-muted-foreground">/mo</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Ideal for children beginning their social journey in a structured, gentle environment.
            </p>

            <Separator className="my-8 bg-border" />

            <ul className="flex flex-1 flex-col gap-4">
              {starterFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[var(--chart-2)] to-[var(--primary)] text-primary-foreground">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  <span className="leading-relaxed">{feature}</span>
                </li>)}
            </ul>

            <Button asChild variant="outline" size="lg" className="mt-10 w-full border-border bg-transparent text-foreground hover:bg-accent">
              <Link to="/FeesEnrollmentPage">View Details</Link>
            </Button>
          </div>

          {}
          <div data-index={1} className="relative flex flex-col lg:scale-105">
            {}
            <div className="relative rounded-3xl p-[1.5px] bg-[conic-gradient(from_0deg,var(--primary),var(--chart-2),var(--chart-3),var(--primary))] [animation:spin_8s_linear_infinite]">
              <div className="relative rounded-3xl bg-card p-10">
                {}
                <div className="pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br from-[var(--primary)]/10 via-transparent to-[var(--chart-2)]/10" aria-hidden="true" />

                <div className="relative">
                  {}
                  <div className="absolute -top-[68px] left-1/2 -translate-x-1/2">
                    <div className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[var(--primary)] to-[var(--chart-2)] px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary-foreground shadow-lg">
                      <Sparkles className="size-3" />
                      Most Popular
                    </div>
                  </div>

                  <div className="font-mono text-xs uppercase tracking-widest text-primary">
                    Full-Day
                  </div>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                      $1,450
                    </span>
                    <span className="text-base text-muted-foreground">/mo</span>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    A comprehensive experience for children ready for deeper exploration and community.
                  </p>

                  <Separator className="my-8 bg-border" />

                  <ul className="flex flex-col gap-4">
                    {proFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[var(--chart-2)] to-[var(--primary)] text-primary-foreground shadow">
                          <Check className="size-3" strokeWidth={3} />
                        </span>
                        <span className="leading-relaxed">{feature}</span>
                      </li>)}
                  </ul>

                  <Button asChild size="lg" className="mt-10 w-full bg-gradient-to-r from-[var(--primary)] to-[var(--chart-2)] text-primary-foreground shadow-lg hover:opacity-90">
                    <Link to="/FeesEnrollmentPage">Apply Now</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {}
          <div data-index={2} className="relative flex flex-col overflow-hidden rounded-3xl border border-border bg-card p-10 shadow-lg">
            {}
            <div className="pointer-events-none absolute inset-0 opacity-[0.15]" aria-hidden="true" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, var(--muted-foreground) 1px, transparent 0)",
            backgroundSize: "24px 24px"
          }} />

            <div className="relative">
              <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                3-Day
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-default text-6xl font-bold tracking-tight text-foreground">
                  $1,100
                </span>
                <span className="text-base text-muted-foreground">/mo</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                A balanced schedule providing consistency while allowing for family time.
              </p>

              <Separator className="my-8 bg-border" />

              <ul className="flex flex-1 flex-col gap-4">
                {agencyFeatures.map((feature, i) => <li data-index={i} key={i} className="flex items-start gap-3 text-sm text-foreground">
                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-[var(--chart-2)] to-[var(--primary)] text-primary-foreground">
                      <Check className="size-3" strokeWidth={3} />
                    </span>
                    <span className="leading-relaxed">{feature}</span>
                  </li>)}
              </ul>

              <Button asChild variant="outline" size="lg" className="mt-10 w-full border-border bg-transparent text-foreground hover:bg-accent">
                <Link to="/ContactUsPage">Request Info</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div ref={reassuranceRef} className={`mt-16 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 transition-all duration-700 ease-out ${reassuranceVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          {reassurance.map((item, i) => <div data-index={i} key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="flex size-5 items-center justify-center rounded-md bg-gradient-to-br from-[var(--chart-2)] to-[var(--primary)] text-primary-foreground">
                <Check className="size-3" strokeWidth={3} />
              </span>
              <span>{item}</span>
            </div>)}
        </div>
      </div>
    </section>;
}